<section class="patient-discover">
  <aside class="patient-discover__filters">
    <header class="patient-discover__filters-header">
      <h3>{{ 'PATIENT_SPACE.DISCOVER.TITLE' | translate }}</h3>
      <p>{{ 'PATIENT_SPACE.DISCOVER.SUBTITLE' | translate }}</p>
    </header>

    <div class="patient-discover__field">
      <label>{{ 'PATIENT_SPACE.DISCOVER.FILTERS.SEARCH' | translate }}</label>
      <input
        pInputText
        [(ngModel)]="filters.q"
        [placeholder]="'PATIENT_SPACE.DISCOVER.FILTERS.SEARCH_PLACEHOLDER' | translate"
        (keyup.enter)="loadCenters(1)"
      />
    </div>

    <div class="patient-discover__field">
      <label>{{ 'PATIENT_SPACE.DISCOVER.FILTERS.CATEGORIES' | translate }}</label>
      <p-multiSelect
        [options]="bootstrap.categories"
        [(ngModel)]="filters.category_ids"
        (onChange)="loadCenters(1)"
        optionLabel="displayName"
        optionValue="id"
        [filter]="true"
        [filterBy]="'displayName'"
        [showToggleAll]="true"
        [placeholder]="'PATIENT_SPACE.DISCOVER.FILTERS.CATEGORIES_PLACEHOLDER' | translate"
        styleClass="w-full"
      ></p-multiSelect>
    </div>

    <div class="patient-discover__field">
      <label>{{ 'PATIENT_SPACE.DISCOVER.FILTERS.PROVINCE' | translate }}</label>
      <p-select
        [options]="bootstrap.provinces"
        [(ngModel)]="filters.province"
        (onChange)="loadCenters(1)"
        [placeholder]="'PATIENT_SPACE.DISCOVER.FILTERS.PROVINCE_PLACEHOLDER' | translate"
        [showClear]="true"
        styleClass="w-full"
      ></p-select>
    </div>

    <div class="patient-discover__location">
      <button pButton type="button" size="small" (click)="useMyLocation()">
        {{ 'PATIENT_SPACE.DISCOVER.FILTERS.USE_LOCATION' | translate }}
      </button>
      @if (filters.lat !== null && filters.lng !== null) {
        <button pButton type="button" size="small" severity="secondary" (click)="removeMyLocation()">
          {{ 'PATIENT_SPACE.DISCOVER.FILTERS.CLEAR_LOCATION' | translate }}
        </button>
      }
    </div>

    @if (filters.lat !== null && filters.lng !== null) {
      <div class="patient-discover__field">
        <label>{{ 'PATIENT_SPACE.DISCOVER.FILTERS.RADIUS' | translate }}</label>
        <p-select
          [options]="[5, 10, 25, 50, 100]"
          [(ngModel)]="filters.radius_km"
          (onChange)="loadCenters(1)"
          styleClass="w-full"
        ></p-select>
      </div>
    }

    <div class="patient-discover__actions">
      <button pButton type="button" (click)="loadCenters(1)" [disabled]="loadingBootstrap || loadingCenters">
        {{ 'COMMON.APPLY' | translate }}
      </button>
      <button pButton type="button" severity="secondary" (click)="clearFilters()">
        {{ 'COMMON.CLEAR' | translate }}
      </button>
    </div>
  </aside>

  <article class="patient-discover__results">
    <div class="patient-discover__map" leaflet [leafletOptions]="mapOptions" [leafletLayers]="mapLayers" (leafletMapReady)="onMapReady($event)"></div>

    @if (loadingCenters) {
      <p class="patient-discover__state">{{ 'COMMON.LOADING' | translate }}</p>
    } @else if (!centers.length) {
      <p class="patient-discover__state">{{ 'PATIENT_SPACE.DISCOVER.EMPTY' | translate }}</p>
    } @else {
      <div class="patient-discover__list">
        @for (center of centers; track center.id) {
          <article class="patient-discover__card" [class.is-selected]="selectedCenter?.id === center.id">
            <header>
              <div>
                <h4>{{ center.name }}</h4>
                <p class="patient-discover__address">
                  {{ center.address || '-' }}
                  @if (center.city || center.province) {
                    · {{ center.city || '' }} {{ center.province || '' }}
                  }
                </p>
              </div>
              <span class="patient-discover__rating">
                <i class="fas fa-star"></i>
                {{ center.rating.average || '-' }} ({{ center.rating.count }})
              </span>
            </header>

            <div class="patient-discover__contact">
              @if (center.phone) {
                <a [href]="'tel:' + center.phone">
                  <i class="fas fa-phone"></i>
                  <span>{{ center.phone }}</span>
                </a>
              }
              @if (center.email) {
                <a [href]="'mailto:' + center.email">
                  <i class="fas fa-envelope"></i>
                  <span>{{ center.email }}</span>
                </a>
              }
              @if (center.distance_km !== null && center.distance_km !== undefined) {
                <span class="patient-discover__distance-chip">
                  {{ 'PATIENT_SPACE.DISCOVER.DISTANCE' | translate }}: {{ center.distance_km | number: '1.1-1' }} km
                </span>
              }
            </div>

            <p class="patient-discover__categories">
              @for (category of center.categories; track category.id) {
                <span>{{ categoryLabel(category) }}</span>
              }
            </p>

            <div class="patient-discover__card-actions">
              <button pButton type="button" severity="secondary" size="small" (click)="selectCenter(center, 'reviews')">
                {{ 'PATIENT_SPACE.DISCOVER.SEE_REVIEWS' | translate }}
              </button>
              @if (canReserve(center)) {
                <button pButton type="button" size="small" (click)="reserve(center)">
                  {{ 'PATIENT_SPACE.DISCOVER.BOOK' | translate }}
                </button>
              }
            </div>
          </article>
        }
      </div>
      @if (total > size) {
        <p-paginator
          [first]="first"
          [rows]="size"
          [totalRecords]="total"
          [rowsPerPageOptions]="[10, 20, 40]"
          (onPageChange)="onCentersPageChange($event)"
        ></p-paginator>
      }
    }
  </article>
</section>

@if (showCenterDrawer && selectedCenter) {
  <button class="patient-discover__drawer-overlay" (click)="closeCenterDrawer()" aria-label="close"></button>

  <aside class="patient-discover__drawer">
    <header class="patient-discover__drawer-header">
      <div>
        <h4>{{ selectedCenter.name }}</h4>
        <p>
          {{ selectedCenter.address || '-' }}
          @if (selectedCenter.city || selectedCenter.province) {
            · {{ selectedCenter.city || '' }} {{ selectedCenter.province || '' }}
          }
        </p>
      </div>
      <button
        pButton
        type="button"
        severity="secondary"
        icon="pi pi-times"
        styleClass="patient-discover__drawer-close"
        (click)="closeCenterDrawer()"
      ></button>
    </header>

    <div class="patient-discover__drawer-meta">
      <span>
        {{ 'PATIENT_SPACE.DISCOVER.REVIEW.SUMMARY' | translate }}:
        <strong>{{ selectedCenterReviewSummary.average || '-' }}</strong>
        ({{ selectedCenterReviewSummary.count }})
      </span>
      @if (selectedCenter.distance_km !== null && selectedCenter.distance_km !== undefined) {
        <span>
          {{ 'PATIENT_SPACE.DISCOVER.DISTANCE' | translate }}:
          {{ selectedCenter.distance_km | number: '1.1-1' }} km
        </span>
      }
    </div>

    <div class="patient-discover__drawer-contact">
      @if (selectedCenter.phone) {
        <a [href]="'tel:' + selectedCenter.phone">
          <i class="fas fa-phone"></i>
          <span>{{ selectedCenter.phone }}</span>
        </a>
      }
      @if (selectedCenter.email) {
        <a [href]="'mailto:' + selectedCenter.email">
          <i class="fas fa-envelope"></i>
          <span>{{ selectedCenter.email }}</span>
        </a>
      }
    </div>

    <div class="patient-discover__drawer-actions">
      <div class="patient-discover__drawer-tabs">
        <button
          type="button"
          class="patient-discover__drawer-tab"
          [class.is-active]="centerDrawerTab === 'summary'"
          (click)="centerDrawerTab = 'summary'"
        >
          {{ 'COMMON.DETAILS' | translate }}
        </button>
        <button
          type="button"
          class="patient-discover__drawer-tab"
          [class.is-active]="centerDrawerTab === 'reviews'"
          (click)="centerDrawerTab = 'reviews'"
        >
          {{ 'PATIENT_SPACE.DISCOVER.SEE_REVIEWS' | translate }}
        </button>
      </div>
      @if (canReserve(selectedCenter)) {
        <button pButton type="button" size="small" (click)="reserve(selectedCenter)" styleClass="patient-discover__drawer-book-btn">
          {{ 'PATIENT_SPACE.DISCOVER.BOOK' | translate }}
        </button>
      }
    </div>

    @if (centerDrawerTab === 'summary') {
      <section class="patient-discover__drawer-summary patient-discover__drawer-panel">
        <div class="patient-discover__summary-rating">
          <div class="patient-discover__summary-stars">
            @for (star of ratingStars; track star) {
              <i class="fas fa-star" [class.is-active]="star <= getAverageRatingRounded()"></i>
            }
          </div>
          <p>
            {{ 'PATIENT_SPACE.DISCOVER.REVIEW.SUMMARY' | translate }}:
            <strong>{{ selectedCenterReviewSummary.average || '-' }}</strong>
            ({{ selectedCenterReviewSummary.count }})
          </p>
        </div>
        <p class="patient-discover__categories patient-discover__categories--drawer">
          @for (category of selectedCenter.categories; track category.id) {
            <span>{{ categoryLabel(category) }}</span>
          }
        </p>
      </section>
    } @else {
      <section class="patient-discover__review-form patient-discover__drawer-panel">
        <label>{{ 'PATIENT_SPACE.DISCOVER.REVIEW.RATING' | translate }}</label>
        <div class="patient-discover__rating-picker" role="radiogroup">
          @for (star of ratingStars; track star) {
            <button
              type="button"
              class="patient-discover__rating-star"
              [class.is-active]="star <= ownReview.rating"
              (click)="setOwnRating(star)"
              [attr.aria-label]="star + ' stars'"
            >
              <i class="fas fa-star"></i>
            </button>
          }
          <span class="patient-discover__rating-value">{{ ownReview.rating }}/5</span>
        </div>

        <label>{{ 'PATIENT_SPACE.DISCOVER.REVIEW.COMMENT' | translate }}</label>
        <textarea
          rows="3"
          [(ngModel)]="ownReview.comment"
          [placeholder]="'PATIENT_SPACE.DISCOVER.REVIEW.COMMENT_PLACEHOLDER' | translate"
        ></textarea>

        @if (ownReview.status) {
          <small>
            {{ 'PATIENT_SPACE.DISCOVER.REVIEW.STATUS' | translate }}:
            {{ ('PATIENT_SPACE.DISCOVER.REVIEW_STATUS.' + ownReview.status) | translate }}
          </small>
        }

        <button pButton type="button" (click)="saveReview()" [disabled]="savingReview">
          {{ 'PATIENT_SPACE.DISCOVER.REVIEW.SAVE' | translate }}
        </button>
      </section>

      @if (loadingReviews) {
        <p class="patient-discover__state">{{ 'COMMON.LOADING' | translate }}</p>
      } @else if (!selectedCenterReviews.length) {
        <p class="patient-discover__state">{{ 'PATIENT_SPACE.DISCOVER.REVIEW.EMPTY' | translate }}</p>
      } @else {
        <div class="patient-discover__review-list patient-discover__review-list--drawer">
          @for (review of selectedCenterReviews; track review.id) {
            <article class="patient-discover__review-item">
              <strong>{{ review.patient?.name || '-' }} {{ review.patient?.surname || '' }}</strong>
              <span class="patient-discover__review-item-stars">
                @for (star of ratingStars; track star) {
                  <i class="fas fa-star" [class.is-active]="star <= review.rating"></i>
                }
                <b>{{ review.rating }}/5</b>
              </span>
              <p>{{ review.comment || '-' }}</p>
              <small>{{ review.updated_at | date: 'dd/MM/yyyy HH:mm' }}</small>
            </article>
          }
        </div>
        @if (reviewsTotal > reviewsSize) {
          <p-paginator
            [first]="(reviewsPage - 1) * reviewsSize"
            [rows]="reviewsSize"
            [totalRecords]="reviewsTotal"
            [rowsPerPageOptions]="[5, 10, 20]"
            (onPageChange)="onReviewsPageChange($event)"
          ></p-paginator>
        }
      }
    }
  </aside>
}
